import { LoginError } from "@/types/auth-errors.ts";
import { $api, fetchClient } from "@/utils/fetch.ts";
import { getChallenge } from "@/utils/webauthn.ts";
import { getTracer } from "@/utils/telemetry.ts";

interface AuthResponse {
	token: string;
}

/**
 * Fetch cached query options for auth check.
 * Caches the auth status for 10 minutes to avoid repeated checks.
 * Bearer token is automatically injected via middleware.
 */
export const checkAuthQueryOptions = () =>
	$api.queryOptions("get", "/api/auth/check", undefined, {
		staleTime: 10 * 60 * 1000, // 10 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});

/**
 * Login user with WebAuthn passkey.
 * Throws WebAuthnError if user cancels or device doesn't support passkeys.
 * Throws LoginError if server rejects credentials.
 */
export async function loginUser(): Promise<AuthResponse> {
	const challenge = await getChallenge();

	// Get WebAuthn credentials from device
	const credentials = await getTracer("frontend").startActiveSpan(
		"webauthn",
		async (span) => {
			const result = (await window.navigator.credentials.get({
				publicKey: {
					challenge,
					rpId: window.location.hostname,
					allowCredentials: [],
					userVerification: "required",
				},
			})) as PublicKeyCredential | null;
			span.setAttribute("webauthn.challenge", challenge.toBase64());
			span.setAttribute("webauthn.credential_id", result?.id ?? "unknown");
			span.setAttribute("webauthn.type", result?.type ?? "unknown");
			span.end();
			return result;
		},
	);

	if (!credentials) {
		throw new LoginError("webauthn_cancelled");
	}

	const assertion = credentials.response as AuthenticatorAssertionResponse;

	if (assertion.userHandle === null) {
		throw new LoginError("webauthn_invalid_state");
	}

	const { data, error } = await fetchClient.POST("/api/auth/login", {
		body: {
			userHandle: new Uint8Array(assertion.userHandle).toBase64({
				alphabet: "base64url",
				omitPadding: true,
			}),
			authenticatorData: new Uint8Array(assertion.authenticatorData).toBase64(),
			clientDataJson: new Uint8Array(assertion.clientDataJSON).toBase64(),
			signature: new Uint8Array(assertion.signature).toBase64(),
		},
	});

	if (error) {
		throw new LoginError("general_auth_error");
	}

	// Token is returned and will be handled by AuthProvider
	// AuthProvider calls updateAuthHeaders() before using this token
	return data;
}

/**
 * Ask the backend to turn oneDeploy's gateway identity into a session, which
 * only ever succeeds inside a PR preview.
 *
 * Returns null instead of throwing on every failure, because on all three
 * deployments that are not previews the expected answer is a 404 (the endpoint
 * is not routable) and inside a preview reached without the firewall it is a
 * 401. None of those is an error the visitor should see — they simply mean
 * "carry on to the login page".
 *
 * See `docs/architecture/pr-previews.md`.
 */
export async function loginWithPreviewIdentity(): Promise<string | null> {
	try {
		const { data } = await fetchClient.POST("/api/public/preview-session", {
			body: {},
		});
		return data?.token ?? null;
	} catch {
		// A network failure here is indistinguishable from "not a preview" and has
		// the same handling: fall through to the normal unauthenticated path. The
		// real backend outage is reported by the auth check that follows.
		return null;
	}
}

export const useStartSignupMutation = () =>
	$api.useMutation("post", "/api/auth/signup");

export const useJoinOrgMutation = () =>
	$api.useMutation("post", "/api/auth/invite-setup");

export const useStartRecoverMutation = () =>
	$api.useMutation("post", "/api/auth/reset");

export const useResetAuthMutation = () =>
	$api.useMutation("post", "/api/auth/reset-setup");

export const useUpdateProfileMutation = () =>
	$api.useMutation("put", "/api/auth/profile");

export const useUpdatePreferencesMutation = () =>
	$api.useMutation("patch", "/api/auth/preferences");

export const useUpdateNewsletterMutation = () =>
	$api.useMutation("put", "/api/auth/newsletter");

export const totpDevicesQueryOptions = () =>
	$api.queryOptions("get", "/api/auth/totp");

export const useTotpAddMutation = () =>
	$api.useMutation("post", "/api/auth/totp");

export const useTotpDeleteMutation = () =>
	$api.useMutation("post", "/api/auth/totp/delete");
